import React from "react";
import { format } from "date-fns";

// Types
import { TestOrderPrintData, PrintableTestEntry } from "../../../types/healthcare/TestTypes";

export type PrintFormat = "pathology_request" | "medicare_request" | "individual" | "batch" | "summary";

export interface PrintableOrderOptions {
	includePatientDetails: boolean;
	includeOrderingProvider: boolean;
	includeTestDetails: boolean;
	includeSpecialInstructions: boolean;
	includeCompanyLogo: boolean;
	printFormat: PrintFormat;
	orientation: "portrait" | "landscape";
	fontSize: "small" | "medium" | "large";
}

interface PrintableOrderProps {
	/**
	 * The assembled print payload, one entry per order — see
	 * `fetchTestOrderPrintData`. This is the SINGLE data source: the component never
	 * reads a `Test` object, because several surfaces hold one with no `patient`
	 * attached and passing those through is what made the patient block a dead branch.
	 */
	orders: TestOrderPrintData[];
	options: PrintableOrderOptions;
}

/**
 * ⚠️ COLUMN THRESHOLDS — an order can carry 50+ test types.
 *
 * A comprehensive panel expands to dozens of analysis types, and `test_type` holds the
 * union of every ordered one. Until this component resolved them, they were concatenated
 * into a single unreadable string, which hid the volume problem entirely — a real list of
 * 50 items is the NORMAL case on a request slip, not an edge case.
 *
 * Past the single-column threshold the list is also grouped by test category: a flat
 * 50-item list is impractical for collection-centre staff to check off.
 */
const SINGLE_COLUMN_MAX = 12;
const TWO_COLUMN_MAX = 30;

const columnCountFor = (testCount: number): 1 | 2 | 3 => {
	if (testCount <= SINGLE_COLUMN_MAX) return 1;
	if (testCount <= TWO_COLUMN_MAX) return 2;
	return 3;
};

/** Past this many tests the batch/summary worksheets truncate instead of listing inline. */
const INLINE_NAME_LIMIT = 3;

const PrintableOrder: React.FC<PrintableOrderProps> = ({ orders, options }) => {
	const formatDate = (date: string | Date | null | undefined) => {
		if (!date) return "Not specified";
		try {
			return format(new Date(date), "dd/MM/yyyy HH:mm");
		} catch {
			return "Invalid date";
		}
	};

	const formatDateShort = (date: string | Date | null | undefined) => {
		if (!date) return "Not specified";
		try {
			return format(new Date(date), "dd/MM/yyyy");
		} catch {
			return "Invalid date";
		}
	};

	/** A request slip leaves an unknown field BLANK to be filled in by hand — never "N/A". */
	const orDash = (value: string | null | undefined) => (value && value.trim() !== "" ? value : "");

	const getPriorityBadgeClass = (priority: string) => {
		switch (priority?.toLowerCase()) {
			case "urgent":
				return "priority-urgent";
			case "stat":
				return "priority-stat";
			default:
				return "priority-routine";
		}
	};

	const orderReference = (order: TestOrderPrintData) => order.sample_id || `#${order.id}`;

	const patientFullName = (order: TestOrderPrintData) => {
		const p = order.patient;
		if (!p) return "";
		return [p.title, p.first_name, p.middle_name, p.last_name].filter(Boolean).join(" ");
	};

	const patientAddress = (order: TestOrderPrintData) => {
		const p = order.patient;
		if (!p) return "";
		return [p.address, p.city, p.state, p.postal_code].filter(Boolean).join(", ");
	};

	const providerName = (order: TestOrderPrintData) => {
		const d = order.ordering_provider;
		if (!d) return "";
		return [d.title, d.first_name, d.last_name].filter(Boolean).join(" ");
	};

	/**
	 * Medicare number grouped #### ##### # — the shape printed on the card.
	 *
	 * ⚠️ Zero-padded to 10 first. The column is a bigInteger server-side and is serialised
	 * as a string precisely so the grouping is done on digits, not on a JS number.
	 */
	const formatMedicare = (value: string | null | undefined) => {
		if (!value) return "";
		const digits = value.replace(/\D/g, "");
		if (digits.length === 0) return "";
		const padded = digits.padStart(10, "0");
		return `${padded.slice(0, 4)} ${padded.slice(4, 9)} ${padded.slice(9)}`;
	};

	// -------------------------------------------------------------------------
	// The requested-tests list — ONE implementation, shared by every layout.
	// -------------------------------------------------------------------------

	const groupByCategory = (tests: PrintableTestEntry[]) => {
		const groups = new Map<string, PrintableTestEntry[]>();
		tests.forEach((t) => {
			const key = t.category_name || "Other";
			if (!groups.has(key)) groups.set(key, []);
			groups.get(key)!.push(t);
		});
		return Array.from(groups.entries());
	};

	/**
	 * The requested tests, one entry per ordered test type, by display name.
	 *
	 * `ticked` renders a check box for collection-centre staff to work through; the
	 * internal layouts use the plain variant.
	 */
	const renderTestList = (order: TestOrderPrintData, ticked: boolean) => {
		const tests = order.tests || [];

		if (tests.length === 0) {
			return <div className="test-list-empty">No tests recorded on this order</div>;
		}

		const columns = columnCountFor(tests.length);
		const item = (t: PrintableTestEntry) => (
			<div className="test-list-item" key={t.value}>
				{ticked && <span className="test-tick" />}
				<span className="test-list-name">{t.name}</span>
			</div>
		);

		// Single column stays flat — grouping a short list adds headings without adding
		// scannability, and costs vertical space a one-page slip does not have.
		if (columns === 1) {
			return <div className="test-list test-list--cols-1">{tests.map(item)}</div>;
		}

		return (
			<div className={`test-list test-list--cols-${columns}`}>
				{groupByCategory(tests).map(([category, entries]) => (
					<div className="test-group" key={category}>
						<div className="test-group-title">{category}</div>
						{entries.map(item)}
					</div>
				))}
			</div>
		);
	};

	/** Test names for a worksheet row, truncated so one order stays one line tall. */
	const summariseTestNames = (order: TestOrderPrintData) => {
		const names = (order.tests || []).map((t) => t.name);
		if (names.length === 0) return "No tests recorded";
		if (names.length <= INLINE_NAME_LIMIT) return names.join(", ");
		return `${names.slice(0, INLINE_NAME_LIMIT).join(", ")} + ${names.length - INLINE_NAME_LIMIT} more`;
	};

	const renderPackagesAndWarnings = (order: TestOrderPrintData) => (
		<>
			{order.custom_test_packages.length > 0 && (
				<div className="req-packages">
					<span className="req-packages-label">Package{order.custom_test_packages.length !== 1 ? "s" : ""}:</span>{" "}
					{order.custom_test_packages.map((p) => p.name).join(", ")}
				</div>
			)}
			{(order.requires_fasting || order.requires_contrast) && (
				<div className="req-warning">
					{order.requires_fasting && <div>⚠ FASTING REQUIRED — 10–12 hours, water only</div>}
					{order.requires_contrast && <div>⚠ CONTRAST REQUIRED — check preparation instructions</div>}
				</div>
			)}
		</>
	);

	/**
	 * Identifies the patient at the top of the slip.
	 *
	 * ⚠️ It does NOT repeat on pages 2+, and that is a decision rather than an omission —
	 * see the note in `PrintOrderModal.getPrintStyles()`. Every pure-CSS way to repeat it
	 * either overlays the content beneath it or breaks the multi-column test list, and on
	 * a 50-test request an overlay that hides test names is the worse failure.
	 */
	const renderContinuationBar = (order: TestOrderPrintData) => (
		<div className="req-continuation">
			<span>{patientFullName(order) || "Patient"}</span>
			<span>
				DOB {order.patient?.date_of_birth ? formatDateShort(order.patient.date_of_birth) : "—"} · Ref {orderReference(order)}
			</span>
		</div>
	);

	const renderSignatureBlock = (order: TestOrderPrintData) => (
		<div className="req-signature">
			<div className="req-signature-row">
				<span className="req-field-label">Requested by</span>
				<span className="req-rule">{orDash(providerName(order))}</span>
			</div>
			<div className="req-signature-row">
				<span className="req-field-label">Provider No.</span>
				<span className="req-rule">{orDash(order.ordering_provider?.provider_number)}</span>
			</div>
			<div className="req-signature-row">
				{/* No practitioner signature is stored anywhere on the platform — this is a
				    ruled line to be signed by hand, never an image and never a typed name. */}
				<span className="req-field-label">Signature</span>
				<span className="req-rule" />
				<span className="req-field-label req-field-label--date">Date</span>
				<span className="req-rule req-rule--short">{format(new Date(), "dd/MM/yyyy")}</span>
			</div>
		</div>
	);

	// -------------------------------------------------------------------------
	// Layout: Pathology Request (clinic referral slip)
	// -------------------------------------------------------------------------

	const renderPathologyRequests = () =>
		orders.map((order) => (
			<div key={order.id} className="print-page req-page">
				{renderContinuationBar(order)}

				<div className="req-letterhead">
					{/* There is no clinic logo column anywhere — the letterhead is text. */}
					<div className="req-clinic-name">{(options.includeCompanyLogo && order.clinic?.name) || "Pathology Request"}</div>
					{order.clinic && (
						<div className="req-clinic-meta">
							{[order.clinic.address, order.clinic.phone, order.clinic.email].filter(Boolean).join(" · ")}
						</div>
					)}
				</div>

				<div className="req-title">
					PATHOLOGY REQUEST
					{["urgent", "stat"].includes(order.priority?.toLowerCase()) && <span className="req-urgent">URGENT</span>}
				</div>

				{options.includePatientDetails && (
					<div className="req-section">
						<div className="req-field-row">
							<span className="req-field-label">Patient</span>
							<span className="req-rule">{orDash(patientFullName(order))}</span>
							<span className="req-field-label req-field-label--date">DOB</span>
							<span className="req-rule req-rule--short">
								{order.patient?.date_of_birth ? formatDateShort(order.patient.date_of_birth) : ""}
							</span>
						</div>
						<div className="req-field-row">
							<span className="req-field-label">Sex</span>
							<span className="req-rule req-rule--short">{orDash(order.patient?.sex || order.patient?.gender)}</span>
							<span className="req-field-label req-field-label--date">Ref</span>
							<span className="req-rule req-rule--short">{orderReference(order)}</span>
						</div>
						<div className="req-field-row">
							<span className="req-field-label">Address</span>
							<span className="req-rule">{orDash(patientAddress(order))}</span>
						</div>
						{order.patient?.phone && (
							<div className="req-field-row">
								<span className="req-field-label">Phone</span>
								<span className="req-rule">{order.patient.phone}</span>
							</div>
						)}
					</div>
				)}

				{renderPackagesAndWarnings(order)}

				<div className="req-section req-tests">
					<div className="req-section-title">Requested tests ({order.tests.length})</div>
					{renderTestList(order, true)}
				</div>

				{options.includeSpecialInstructions && (order.reason || order.billable_diagnosis) && (
					<div className="req-section">
						<div className="req-section-title">Clinical notes</div>
						<div className="req-notes">{[order.reason, order.billable_diagnosis].filter(Boolean).join(" · ")}</div>
					</div>
				)}

				{order.facility && (
					<div className="req-field-row">
						<span className="req-field-label">Collect at</span>
						<span className="req-rule">{order.facility}</span>
					</div>
				)}

				{options.includeOrderingProvider && renderSignatureBlock(order)}
			</div>
		));

	// -------------------------------------------------------------------------
	// Layout: Medicare-style pathology request
	//
	// ⚠️ This reproduces the LAYOUT AND FIELDS of a standard Australian pathology
	// request. It is not an official Medicare-issued form, carries no form serial
	// number, and nothing here validates a provider number against Medicare. Whether a
	// given collection centre accepts it is a clinical/commercial question.
	// -------------------------------------------------------------------------

	const renderMedicareRequests = () =>
		orders.map((order) => {
			const p = order.patient;
			const isUrgent = ["urgent", "stat"].includes(order.priority?.toLowerCase());

			return (
				<div key={order.id} className="print-page req-page">
					{renderContinuationBar(order)}

					<div className="req-title req-title--boxed">
						REQUEST FOR PATHOLOGY
						<span className={`req-checkbox ${isUrgent ? "req-checkbox--ticked" : ""}`}>URGENT</span>
					</div>

					<div className="req-section">
						<div className="req-section-title">Requesting practitioner</div>
						<div className="req-field-row">
							<span className="req-field-label">Name</span>
							<span className="req-rule">{orDash(providerName(order))}</span>
							<span className="req-field-label req-field-label--date">Provider No.</span>
							<span className="req-rule req-rule--short">{orDash(order.ordering_provider?.provider_number)}</span>
						</div>
						<div className="req-field-row">
							<span className="req-field-label">Practice</span>
							<span className="req-rule">
								{orDash([order.clinic?.name, order.clinic?.address].filter(Boolean).join(", "))}
							</span>
						</div>
					</div>

					<div className="req-section">
						<div className="req-section-title">Patient</div>
						<div className="req-field-row">
							{/* Surname and given names are separate, upper-cased fields — the
							    convention on these forms and how collection staff read them. */}
							<span className="req-field-label">Surname</span>
							<span className="req-rule">{orDash(p?.last_name?.toUpperCase())}</span>
							<span className="req-field-label req-field-label--date">Given</span>
							<span className="req-rule req-rule--short">
								{orDash([p?.first_name, p?.middle_name].filter(Boolean).join(" "))}
							</span>
						</div>
						<div className="req-field-row">
							<span className="req-field-label">DOB</span>
							<span className="req-rule req-rule--short">{p?.date_of_birth ? formatDateShort(p.date_of_birth) : ""}</span>
							<span className="req-field-label req-field-label--date">Sex</span>
							<span className="req-rule req-rule--short">{orDash(p?.sex || p?.gender)}</span>
						</div>
						<div className="req-field-row">
							<span className="req-field-label">Medicare No.</span>
							<span className="req-rule req-rule--mono">{formatMedicare(p?.medicare_number)}</span>
							<span className="req-field-label req-field-label--date">IRN</span>
							<span className="req-rule req-rule--tiny">{orDash(p?.medicare_individual_ref_number)}</span>
							<span className="req-field-label req-field-label--date">Valid to</span>
							{/* medicare_valid_to is a STRING column ("08/28") — never parsed as a date. */}
							<span className="req-rule req-rule--tiny">{orDash(p?.medicare_valid_to)}</span>
						</div>
						<div className="req-field-row">
							<span className="req-field-label">Address</span>
							<span className="req-rule">{orDash(patientAddress(order))}</span>
						</div>
						<div className="req-flags">
							<span className={`req-checkbox ${order.requires_fasting ? "req-checkbox--ticked" : ""}`}>Fasting</span>
							{/* Pensioner and DVA are deliberately blank — we hold neither reliably,
							    and pre-filling one would need a new egress decision, not a guess. */}
							<span className="req-checkbox">Pensioner</span>
							<span className="req-checkbox">DVA</span>
						</div>
					</div>

					{renderPackagesAndWarnings(order)}

					<div className="req-section req-tests">
						<div className="req-section-title">Tests requested ({order.tests.length})</div>
						{renderTestList(order, true)}
					</div>

					{options.includeSpecialInstructions && (order.reason || order.billable_diagnosis) && (
						<div className="req-section">
							<div className="req-section-title">Clinical notes</div>
							<div className="req-notes">{[order.reason, order.billable_diagnosis].filter(Boolean).join(" · ")}</div>
						</div>
					)}

					<div className="req-field-row">
						{/* Always a blank ruled line — a test order has no referring-doctor field. */}
						<span className="req-field-label">Copy to</span>
						<span className="req-rule" />
					</div>

					{renderSignatureBlock(order)}
				</div>
			);
		});

	// -------------------------------------------------------------------------
	// Internal layouts (unchanged in purpose — these are worksheets, not slips)
	// -------------------------------------------------------------------------

	const renderHeader = () => (
		<div className="print-header">
			<div className="print-logo">{(options.includeCompanyLogo && orders[0]?.clinic?.name) || "Healthcare System"}</div>
			<div className="print-date">Printed: {format(new Date(), "dd/MM/yyyy HH:mm")}</div>
		</div>
	);

	const renderIndividualOrders = () =>
		orders.map((order) => (
			<div key={order.id} className="print-page">
				{renderHeader()}
				<div className="order-card">
					<div className="order-header">
						<div>Test Order #{order.id}</div>
						<div>{formatDateShort(order.created_at)}</div>
					</div>
					<div className="order-content">
						{options.includePatientDetails && order.patient && (
							<div className="patient-section">
								<div className="section-title">Patient Information</div>
								<div className="info-grid">
									<div className="info-item">
										<div className="info-label">Patient Name</div>
										<div className="info-value">{patientFullName(order)}</div>
									</div>
									<div className="info-item">
										<div className="info-label">Date of Birth</div>
										<div className="info-value">{formatDateShort(order.patient.date_of_birth)}</div>
									</div>
									<div className="info-item">
										<div className="info-label">Sex</div>
										<div className="info-value">{order.patient.sex || order.patient.gender || "Not specified"}</div>
									</div>
									{order.patient.phone && (
										<div className="info-item">
											<div className="info-label">Phone</div>
											<div className="info-value">{order.patient.phone}</div>
										</div>
									)}
									{order.patient.email && (
										<div className="info-item">
											<div className="info-label">Email</div>
											<div className="info-value">{order.patient.email}</div>
										</div>
									)}
								</div>
							</div>
						)}

						{options.includeTestDetails && (
							<div className="test-details">
								<div className="section-title">Requested Tests ({order.tests.length})</div>
								{renderTestList(order, false)}

								<div className="info-grid" style={{ marginTop: "12px" }}>
									<div className="info-item">
										<div className="info-label">Priority</div>
										<div className="info-value">
											<span className={`priority-badge ${getPriorityBadgeClass(order.priority)}`}>
												{order.priority?.toUpperCase() || "ROUTINE"}
											</span>
										</div>
									</div>
									<div className="info-item">
										<div className="info-label">Status</div>
										<div className="info-value">{order.status?.replace("_", " ").toUpperCase()}</div>
									</div>
									<div className="info-item">
										<div className="info-label">Sample Type</div>
										<div className="info-value">{order.sample_type || "Not specified"}</div>
									</div>
									{order.sample_id && (
										<div className="info-item">
											<div className="info-label">Sample ID</div>
											<div className="info-value">{order.sample_id}</div>
										</div>
									)}
									{order.facility && (
										<div className="info-item">
											<div className="info-label">Facility</div>
											<div className="info-value">{order.facility}</div>
										</div>
									)}
									{order.billable_diagnosis && (
										<div className="info-item">
											<div className="info-label">Diagnosis (ICD-10)</div>
											<div className="info-value">{order.billable_diagnosis}</div>
										</div>
									)}
								</div>

								{order.details && Object.keys(order.details).length > 0 && (
									<div style={{ marginTop: "12px" }}>
										<div className="section-title">Additional Test Information</div>
										<div className="info-grid">
											{Object.entries(order.details).map(([key, value]) => (
												<div key={key} className="info-item">
													<div className="info-label">{key.replace(/_/g, " ").toUpperCase()}</div>
													<div className="info-value">{String(value)}</div>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						)}

						{options.includeOrderingProvider && order.ordering_provider && (
							<div className="provider-section">
								<div className="section-title">Ordering Provider</div>
								<div className="info-grid">
									<div className="info-item">
										<div className="info-label">Provider Name</div>
										<div className="info-value">{providerName(order)}</div>
									</div>
									{order.ordering_provider.provider_number && (
										<div className="info-item">
											<div className="info-label">Provider Number</div>
											<div className="info-value">{order.ordering_provider.provider_number}</div>
										</div>
									)}
								</div>
							</div>
						)}

						{options.includeSpecialInstructions && order.reason && (
							<div className="instructions-section">
								<div className="section-title">Reason for Test / Instructions</div>
								<div className="instructions-text">{order.reason}</div>
							</div>
						)}

						<div className="info-grid" style={{ marginTop: "12px" }}>
							<div className="info-item">
								<div className="info-label">Order Date</div>
								<div className="info-value">{formatDate(order.created_at)}</div>
							</div>
							{order.test_date && (
								<div className="info-item">
									<div className="info-label">Test Date</div>
									<div className="info-value">{formatDate(order.test_date)}</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		));

	const renderBatchPrint = () => (
		<div className="print-page">
			{renderHeader()}
			<div className="batch-summary">
				<h2 style={{ marginBottom: "16px", color: "#007aff" }}>Test Order Batch Print</h2>
				<div className="summary-item">
					<span>Total Orders:</span>
					<span>{orders.length}</span>
				</div>
				<div className="summary-item">
					<span>Print Date:</span>
					<span>{format(new Date(), "dd/MM/yyyy HH:mm")}</span>
				</div>
			</div>

			{orders.map((order) => (
				<div key={order.id} className="order-card">
					<div className="order-header">
						Order #{order.id} - {patientFullName(order)}
					</div>
					<div className="order-content">
						<div className="info-grid">
							<div className="info-item">
								{/* Truncated past the inline limit — a 50-name list would destroy a worksheet row. */}
								<div className="info-label">Tests ({order.tests.length})</div>
								<div className="info-value">{summariseTestNames(order)}</div>
							</div>
							<div className="info-item">
								<div className="info-label">Priority</div>
								<div className="info-value">
									<span className={`priority-badge ${getPriorityBadgeClass(order.priority)}`}>
										{order.priority?.toUpperCase() || "ROUTINE"}
									</span>
								</div>
							</div>
							<div className="info-item">
								<div className="info-label">Status</div>
								<div className="info-value">{order.status?.replace("_", " ").toUpperCase()}</div>
							</div>
							<div className="info-item">
								<div className="info-label">Order Date</div>
								<div className="info-value">{formatDate(order.created_at)}</div>
							</div>
						</div>

						{options.includePatientDetails && order.patient && (
							<div style={{ marginTop: "8px", background: "#f8f9fa", padding: "8px", borderRadius: "4px" }}>
								<div style={{ fontSize: "10px", fontWeight: "bold", marginBottom: "4px", color: "#6c757d" }}>PATIENT DETAILS</div>
								<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
									<span>Ref: {orderReference(order)}</span>
									<span>DOB: {formatDateShort(order.patient.date_of_birth)}</span>
									<span>Sex: {order.patient.sex || order.patient.gender || "N/A"}</span>
								</div>
							</div>
						)}

						{options.includeOrderingProvider && order.ordering_provider && (
							<div style={{ marginTop: "8px", fontSize: "11px", color: "#6c757d" }}>Ordered by: {providerName(order)}</div>
						)}
					</div>
				</div>
			))}
		</div>
	);

	const renderSummaryReport = () => {
		const ordersByPriority = orders.reduce((acc, order) => {
			const priority = order.priority || "routine";
			acc[priority] = (acc[priority] || 0) + 1;
			return acc;
		}, {} as Record<string, number>);

		const ordersByStatus = orders.reduce((acc, order) => {
			const status = order.status || "unknown";
			acc[status] = (acc[status] || 0) + 1;
			return acc;
		}, {} as Record<string, number>);

		return (
			<div className="print-page">
				{renderHeader()}
				<div className="batch-summary">
					<h2 style={{ marginBottom: "16px", color: "#007aff" }}>Test Orders Summary Report</h2>
					<div className="summary-item">
						<span>Total Orders:</span>
						<span>{orders.length}</span>
					</div>
					<div className="summary-item">
						<span>Report Generated:</span>
						<span>{format(new Date(), "dd/MM/yyyy HH:mm")}</span>
					</div>
				</div>

				<div className="order-card">
					<div className="order-header">Orders by Priority</div>
					<div className="order-content">
						{Object.entries(ordersByPriority).map(([priority, count]) => (
							<div key={priority} className="summary-item">
								<span>
									<span className={`priority-badge ${getPriorityBadgeClass(priority)}`}>{priority.toUpperCase()}</span>
								</span>
								<span>{count}</span>
							</div>
						))}
					</div>
				</div>

				<div className="order-card">
					<div className="order-header">Orders by Status</div>
					<div className="order-content">
						{Object.entries(ordersByStatus).map(([status, count]) => (
							<div key={status} className="summary-item">
								<span>{status.replace("_", " ").toUpperCase()}</span>
								<span>{count}</span>
							</div>
						))}
					</div>
				</div>

				<div className="order-card">
					<div className="order-header">Order Details</div>
					<div className="order-content">
						<table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
							<thead>
								<tr style={{ borderBottom: "1px solid #d1d1d6" }}>
									<th style={{ textAlign: "left", padding: "4px" }}>Order ID</th>
									<th style={{ textAlign: "left", padding: "4px" }}>Patient</th>
									<th style={{ textAlign: "left", padding: "4px" }}>Tests</th>
									<th style={{ textAlign: "left", padding: "4px" }}>Priority</th>
									<th style={{ textAlign: "left", padding: "4px" }}>Status</th>
									<th style={{ textAlign: "left", padding: "4px" }}>Order Date</th>
								</tr>
							</thead>
							<tbody>
								{orders.map((order) => (
									<tr key={order.id} style={{ borderBottom: "1px solid #e5e5ea" }}>
										<td style={{ padding: "4px" }}>{order.id}</td>
										<td style={{ padding: "4px" }}>{patientFullName(order)}</td>
										{/* One line per order, whatever the test count. */}
										<td style={{ padding: "4px" }}>{summariseTestNames(order)}</td>
										<td style={{ padding: "4px" }}>
											<span className={`priority-badge ${getPriorityBadgeClass(order.priority)}`} style={{ fontSize: "8px" }}>
												{order.priority?.toUpperCase() || "ROUTINE"}
											</span>
										</td>
										<td style={{ padding: "4px" }}>{order.status?.replace("_", " ").toUpperCase()}</td>
										<td style={{ padding: "4px" }}>{formatDateShort(order.created_at)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		);
	};

	if (!orders || orders.length === 0) {
		return (
			<div className="print-page">
				{renderHeader()}
				<div style={{ textAlign: "center", padding: "40px" }}>
					<div style={{ fontSize: "18px", color: "#8e8e93", marginBottom: "8px" }}>No Orders to Print</div>
					<div style={{ fontSize: "14px", color: "#c7c7cc" }}>Please select orders to print</div>
				</div>
			</div>
		);
	}

	switch (options.printFormat) {
		case "medicare_request":
			return <>{renderMedicareRequests()}</>;
		case "batch":
			return renderBatchPrint();
		case "summary":
			return renderSummaryReport();
		case "individual":
			return <>{renderIndividualOrders()}</>;
		case "pathology_request":
		default:
			return <>{renderPathologyRequests()}</>;
	}
};

export default PrintableOrder;
